import { Injectable } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { AppService } from 'src/app/modules/aa-core/services/app-service/app.service';
import { RoutePlannerDataModel, AALocationModel } from '../../models/route-model';
import { EventsService } from 'src/app/core/services/events.service';
import { AAGoogleMapEvents, AARoutePlannerEvents } from 'src/app/core/events/events';
import { AppError } from 'src/app/core/models/constants';
import { LocationService } from 'src/app/modules/aa-core/services/location-service/location.service';
import { RouteDataStoreService } from 'src/app/modules/aa-core/services/data-store/data-store.service';
import { AppRouterDataService } from 'src/app/modules/app-routing/services/app-router-data/app-router-data.service';
import { AARouterURL } from 'src/app/modules/app-routing/enums/routing.enum';
import { CoreMapLayerService } from 'src/app/modules/aa-core/services/core-map-layer/core-map-layer.service';
import { GoogleMapType } from 'src/app/modules/google-map/enums/google-map-enum';
import { GoogleMapStoreService } from 'src/app/modules/google-map/services/google-map-store/google-map-store.service';
import { GoogleMapStoreModel } from 'src/app/modules/google-map/models/google-map-model';

export interface IRoutePlannerService {
  updateLatitudeLongitude(locationList: Array<AALocationModel>): Observable<any>;
}

@Injectable({
  providedIn: 'root'
})
export class RoutePlannerService extends CoreMapLayerService implements IRoutePlannerService {
  public routeDataModel: RoutePlannerDataModel;
  constructor(
    private dataStore: RouteDataStoreService,
    private locationService: LocationService,
    private appRouterDataServoce: AppRouterDataService,
    private appService: AppService,
    private googleMapStore: GoogleMapStoreService,
    private readonly eventsService: EventsService
  ) {
    super();
    this.routeDataModel = this.dataStore.routeDataModel;
  }

  get googleMapStoreModel(): GoogleMapStoreModel {
    return this.googleMapStore.getGoogleMapModel(GoogleMapType.ROUTE_PLANNER);
  }

  public reset(): void {
    // console.log('***** RESET ROUTE_PLANNER*****');
    this.routeDataModel.resetAll();

    this.eventsService.dispatch(AAGoogleMapEvents.RESET_MAP, GoogleMapType.ROUTE_PLANNER);
  }

  public updateRouterURL(): void {
    this.appRouterDataServoce.updateURL(AARouterURL.ROUTE_PLANNER);
  }

  // Please don't modify this method.
  public updateGoogleMap(skipUpdateRouter: boolean = true): void {
    if (skipUpdateRouter === true) {
      this.updateRouterURL();
      return;
    }
    let locationList: Array<AALocationModel> = [this.routeDataModel.FromLocation, this.routeDataModel.ToLocation];
    locationList.push(...this.routeDataModel.ViaRoutes);

    locationList = locationList.filter((locationModel) => {
      return this.invalidateLocation(locationModel);
    });

    // store from & to location only into localstore  -Call only when address is valid
    // this.routePlannerLocalStore.saveLocationIntoLocalStore(locationList);

    if (locationList.length) {
      this.updateLatitudeLongitude(locationList).subscribe(this.onReadyGeoLocation, (err) => {
        // catch error.
      });
    } else {
      this.onReadyGeoLocation(locationList);
    }
  }

  private invalidateLocation(locationModel: AALocationModel): boolean {
    if (locationModel.isCurrentLocation()) {
      return locationModel.valid() == false;
    } else if (locationModel.isCurrentLocation() == false) {
      return locationModel.valid() == false;
    }
    return false;
  }

  private onReadyGeoLocation = (result?: any) => {
    this.routeDataModel.markViaRoutesAsUsed();
    this.eventsService.dispatch(AAGoogleMapEvents.UPDATE_MAP, GoogleMapType.ROUTE_PLANNER);

    // this.updateRouterURL();
  };

  // Core Method: Please Don't modify
  public updateLatitudeLongitude(locationList: Array<AALocationModel>): Observable<any> {
    const observables: Array<any> = [];

    locationList.forEach((location) => {
      observables.push(this.createLocationObservable(location));
    });

    return new Observable((observer) => {
      forkJoin(observables).subscribe(
        (results: Array<AALocationModel>) => {
          if (locationList.length === results.length) {
            for (let index = 0; index < locationList.length; index++) {
              locationList[index].Latitude = results[index].Latitude;
              locationList[index].Longitude = results[index].Longitude;
              locationList[index].Name = results[index].Name;
              locationList[index].PlaceId = results[index].PlaceId;
              locationList[index].formatted_address = results[index].formatted_address;
              locationList[index].partial_match = results[index].partial_match;
              // if (locationList[index].partial_match) {
              // locationList[index].reset();
              // locationList[index].errorMessage = AppError.ROUTE_LOCATION_NOT_FOUND;
              // this.eventsService.dispatch(AARoutePlannerEvents.DEDUCTED_CHANGES);
              // }
              console.log(locationList);
              if (!locationList[index].Name) {
                locationList[index].errorMessage = index ? AppError.TO_CORD_MISSING : AppError.FROM_CORD_MISSING;
                this.eventsService.dispatch(AARoutePlannerEvents.DEDUCTED_CHANGES);
              }
            }
          }
          observer.next(locationList);
        },
        (err) => {
          console.log('Location not found', err);
          if (err.status === 'INVALID_REQUEST') {
            const loc = locationList.filter((r) => r.Name !== '');
            if (loc.length > 0) {
              loc[0].reset();
              loc[0].errorMessage = AppError.ROUTE_LOCATION_NOT_FOUND;
              this.eventsService.dispatch(AARoutePlannerEvents.DEDUCTED_CHANGES);
            }
          } else {
            const loc = locationList.filter((r) => r.Name === err.address);
            if (loc.length > 0) {
              loc[0].reset();
              loc[0].errorMessage = AppError.ROUTE_LOCATION_NOT_FOUND;
              this.eventsService.dispatch(AARoutePlannerEvents.DEDUCTED_CHANGES);
            }
          }
          observer.error(err);
        }
      );
    });
  }

  private createLocationObservable(locationModel: AALocationModel): Observable<any> {
    if (locationModel.isCurrentLocation()) {
      return this.locationService.loadCurrentLocation(locationModel);
    }
    return this.appService.getGeoLocationByAddress(locationModel);
  }

  public getRouteOrder(): Array<AALocationModel> {
    const displayOrder: Array<AALocationModel> = [];
    displayOrder.push(this.routeDataModel.FromLocation);
    this.routeDataModel.ViaRoutes.forEach((viaRoute: AALocationModel) => {
      displayOrder.push(viaRoute);
    });
    displayOrder.push(this.routeDataModel.ToLocation);

    return displayOrder;
  }
}
